import { ExternalLink, MoreHorizontal, Pencil, Plus } from 'lucide-react';
import type {
  ValueChainDetailsTab,
  ValueChainNode,
  ValueChainRelationType,
} from '@/types/valueChain';

const TABS: Array<{ id: ValueChainDetailsTab; label: string }> = [
  { id: 'details', label: 'Details' },
  { id: 'relations', label: 'Relations' },
  { id: 'sources', label: 'Sources' },
  { id: 'notes', label: 'Notes' },
];

type RelationRow = {
  id: string;
  relation: ValueChainRelationType;
  target: ValueChainNode;
};

type Props = {
  node: ValueChainNode | null;
  tab: ValueChainDetailsTab;
  onTabChange: (tab: ValueChainDetailsTab) => void;
  parent?: ValueChainNode | null;
  childrenCount: number;
  relations: RelationRow[];
  incoming: RelationRow[];
  note: string;
  onNoteChange: (value: string) => void;
  editing: boolean;
  draftDescription: string;
  onToggleEdit: () => void;
  onDraftChange: (value: string) => void;
  onSaveEdit: () => void;
  onAddRelation: () => void;
};

function Field({ label, value }: { label: string; value?: string | number }) {
  if (value == null || value === '') return null;
  return (
    <div className="grid grid-cols-[120px_1fr] gap-3 py-2.5 text-[13px]">
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-gray-900">{value}</dd>
    </div>
  );
}

export function ValueChainDetailsPanel({
  node,
  tab,
  onTabChange,
  parent,
  childrenCount,
  relations,
  incoming: _incoming,
  note,
  onNoteChange,
  editing,
  draftDescription,
  onToggleEdit,
  onDraftChange,
  onSaveEdit,
  onAddRelation,
}: Props) {
  if (!node) {
    return (
      <aside className="hidden h-full w-[360px] shrink-0 flex-col border-l border-gray-200 bg-white lg:flex">
        <p className="p-6 text-sm text-gray-500">Select a node to see details.</p>
      </aside>
    );
  }

  return (
    <aside className="hidden h-full w-[360px] shrink-0 flex-col border-l border-gray-200 bg-white lg:flex">
      <div className="flex items-center gap-5 border-b border-gray-200 px-5">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => onTabChange(item.id)}
            className={`relative py-3 text-sm ${
              tab === item.id ? 'font-medium text-gray-900' : 'text-gray-400 hover:text-gray-700'
            }`}
          >
            {item.label}
            {tab === item.id ? (
              <span className="absolute inset-x-0 -bottom-px h-0.5 bg-gray-900" />
            ) : null}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
        {tab === 'details' || tab === 'relations' || tab === 'sources' ? (
          <>
            <div className="mb-1 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-[22px] font-semibold leading-tight tracking-tight text-gray-900">
                  {node.name}
                </h2>
                <p className="mt-1 text-[13px] text-gray-400">{node.type}</p>
              </div>
              {tab === 'details' ? (
                <button
                  type="button"
                  onClick={editing ? onSaveEdit : onToggleEdit}
                  className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  {editing ? 'Save' : 'Edit'}
                </button>
              ) : null}
            </div>

            {tab === 'details' ? (
              <>
                {editing ? (
                  <textarea
                    value={draftDescription}
                    onChange={(event) => onDraftChange(event.target.value)}
                    className="mt-3 w-full rounded-xl border border-gray-200 px-3 py-2 text-[13px] leading-relaxed text-gray-700 outline-none focus:border-gray-400"
                    rows={5}
                  />
                ) : (
                  <p className="mt-3 text-[13px] leading-relaxed text-gray-600">{node.description}</p>
                )}

                <dl className="mt-5 divide-y divide-gray-100 border-t border-gray-100">
                  <Field label="Type" value={node.type} />
                  <Field label="Parent" value={parent?.name} />
                  <Field label="Children" value={childrenCount} />
                  <Field label="Key Materials" value={node.keyMaterials} />
                  <Field label="Manufacturer" value={node.manufacturer} />
                  <Field label="Notes" value={node.notes} />
                </dl>
              </>
            ) : null}

            {tab === 'details' || tab === 'relations' ? (
              <section className={tab === 'details' ? 'mt-8' : 'mt-2'}>
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-900">Relations</h3>
                  <button
                    type="button"
                    onClick={onAddRelation}
                    className="inline-flex items-center gap-1 text-[13px] text-gray-400 hover:text-gray-900"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add
                  </button>
                </div>
                <div className="space-y-2">
                  {relations.length === 0 ? (
                    <p className="text-[13px] text-gray-400">No relations.</p>
                  ) : (
                    relations.map((row) => (
                      <div
                        key={row.id}
                        className="flex items-center gap-2 rounded-xl border border-gray-100 bg-gray-50/70 px-3 py-2.5"
                      >
                        <span className="text-gray-300">›</span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[13px] text-gray-800">
                            <span className="font-medium tracking-wide text-gray-500">
                              {row.relation}
                            </span>
                            {' → '}
                            {row.target.name}
                          </p>
                        </div>
                        <span className="rounded-md bg-white px-2 py-0.5 text-[11px] text-gray-500 ring-1 ring-gray-200">
                          {row.target.type.split(' / ')[0]}
                        </span>
                        <MoreHorizontal className="h-4 w-4 text-gray-300" />
                      </div>
                    ))
                  )}
                </div>
              </section>
            ) : null}

            {tab === 'details' || tab === 'sources' ? (
              <section className={tab === 'details' ? 'mt-8' : 'mt-2'}>
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-900">Links & References</h3>
                  <span className="inline-flex items-center gap-1 text-[13px] text-gray-300">
                    <Plus className="h-3.5 w-3.5" />
                    Add
                  </span>
                </div>
                <div className="space-y-2">
                  {(node.sources ?? []).length === 0 ? (
                    <p className="text-[13px] text-gray-400">No public sources attached.</p>
                  ) : (
                    (node.sources ?? []).map((source) => (
                      <a
                        key={source.url}
                        href={source.url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-3 rounded-xl border border-gray-100 px-3 py-2.5 hover:border-gray-300"
                      >
                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-[11px] font-semibold text-blue-600">
                          {source.host.split('.')[0]?.slice(0, 2).toUpperCase()}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[13px] text-gray-900">
                            {source.label}
                          </span>
                          <span className="block text-[11px] text-gray-400">{source.host}</span>
                        </span>
                        <ExternalLink className="h-3.5 w-3.5 text-gray-300" />
                      </a>
                    ))
                  )}
                </div>
              </section>
            ) : null}
          </>
        ) : (
          <div>
            <h2 className="text-[22px] font-semibold tracking-tight text-gray-900">{node.name}</h2>
            <p className="mt-1 text-[13px] text-gray-400">Private notes for this node</p>
            <textarea
              value={note}
              onChange={(event) => onNoteChange(event.target.value)}
              placeholder="Add a note..."
              className="mt-4 min-h-[240px] w-full rounded-xl border border-gray-200 px-3 py-2 text-[13px] leading-relaxed text-gray-700 outline-none focus:border-gray-400"
            />
          </div>
        )}
      </div>
    </aside>
  );
}
